import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiRequest } from '@/utils/api'
import { useToast } from 'vue-toastification'

const toast = useToast()

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref(null)
  const token = ref(localStorage.getItem('auth_token'))
  const loading = ref(false)

  // 计算属性
  const isAuthenticated = computed(() => !!token.value && !!user.value)

  // 初始化用户信息
  const initAuth = async () => {
    // 从localStorage尝试恢复用户信息
    const savedUserInfo = localStorage.getItem('user_info')
    if (savedUserInfo && !user.value) {
      try {
        user.value = JSON.parse(savedUserInfo)
      } catch (error) {
        console.error('解析保存的用户信息失败:', error)
        localStorage.removeItem('user_info')
      }
    }
    
    // 如果有token但没有用户信息，则重新获取
    if (token.value && !user.value) {
      try {
        await fetchUserProfile()
      } catch (error) {
        console.error('初始化认证失败:', error)
        logout()
      }
    }
  }

  // 注册
  const register = async (userData) => {
    loading.value = true
    try {
      const response = await apiRequest.post('/api/accounts/auth/register/', userData)
      const data = response.data
      
      // 检查响应格式
      if (data.success) {
        const authToken = data.token
        const userInfo = data.user
        
        // 保存认证信息
        token.value = authToken
        user.value = userInfo
        localStorage.setItem('auth_token', authToken)
        localStorage.setItem('user_info', JSON.stringify(userInfo))
        
        toast.success('注册成功，欢迎使用 BUBU.IM!')
        return data
      } else {
        throw new Error(data.message || '注册失败')
      }
    } catch (error) {
      console.error('注册失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 登录
  const login = async (credentials) => {
    loading.value = true
    try {
      const response = await apiRequest.post('/api/accounts/auth/login/', credentials)
      const data = response.data
      
      // 检查响应格式
      if (data.success) {
        const authToken = data.token
        const userInfo = data.user
        
        // 保存认证信息
        token.value = authToken
        user.value = userInfo
        localStorage.setItem('auth_token', authToken)
        localStorage.setItem('user_info', JSON.stringify(userInfo))
        
        return data
      } else {
        throw new Error(data.message || '登录失败')
      }
    } catch (error) {
      console.error('登录失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 获取用户资料
  const fetchUserProfile = async () => {
    try {
      const response = await apiRequest.get('/api/accounts/user/profile/')
      const data = response.data
      
      if (data.success) {
        user.value = data.data
        localStorage.setItem('user_info', JSON.stringify(data.data))
        return data.data
      } else {
        throw new Error(data.message || '获取用户信息失败')
      }
    } catch (error) {
      console.error('获取用户信息失败:', error)
      throw error
    }
  }

  // 更新用户资料
  const updateProfile = async (profileData) => {
    try {
      const response = await apiRequest.post('/api/accounts/user/profile/', profileData)
      const data = response.data
      
      if (data.success) {
        // 更新本地用户信息
        if (data.data) {
          user.value = { ...user.value, ...data.data }
          localStorage.setItem('user_info', JSON.stringify(user.value))
        } else {
          // 如果后端没有返回数据，重新获取用户资料
          await fetchUserProfile()
        }
        toast.success('资料更新成功')
        return data.data || user.value
      } else {
        throw new Error(data.message || '更新资料失败')
      }
    } catch (error) {
      console.error('更新资料失败:', error)
      throw error
    }
  }

  // 修改密码
  const changePassword = async (passwordData) => {
    try {
      await apiRequest.post('/api/accounts/user/change-password/', passwordData)
      toast.success('密码修改成功')
    } catch (error) {
      console.error('修改密码失败:', error)
      throw error
    }
  }

  // 登出
  const logout = () => {
    // 设置退出登录标志，防止API拦截器显示错误提示
    window.isLoggingOut = true
    
    user.value = null
    token.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_info')
    
    // 清除退出登录标志
    setTimeout(() => {
      window.isLoggingOut = false
    }, 1000)
  }

  // 获取用户统计信息
  const getUserStats = async () => {
    try {
      const response = await apiRequest.get('/api/accounts/stats/')
      return response.data
    } catch (error) {
      console.error('获取用户统计失败:', error)
      throw error
    }
  }

  return {
    // 状态
    user,
    token,
    loading,
    
    // 计算属性
    isAuthenticated,
    
    // 方法
    initAuth,
    register,
    login,
    logout,
    fetchUserProfile,
    updateProfile,
    changePassword,
    getUserStats
  }
})
